import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import { FiClock, FiCalendar } from "react-icons/fi";
import {
  formatDateTimeTrInAppTz,
  formatTimeTrInAppTz,
} from "../../../utils/appTime";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import Button from "../../../components/BaseUI/Button";
import ReportViewOptionsPanel from "../Reports/shared/ReportViewOptionsPanel";
import { useReportFilters } from "../Reports/shared/useReportFilters";
import { todayStr } from "../Reports/shared/utils";
import "../Reports/Reports.scss";
import "../MachineMonitor/MachineMonitor.scss";
import "../DeviceMonitor/DeviceMonitor.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const formatDuration = (ms) => {
  if (ms == null || ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const DonutChart = ({
  percentage = 0,
  size = 160,
  strokeWidth = 18,
  color = "#00c9b7",
  label,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const safePerc = Math.max(0, Math.min(100, percentage));
  const offset = circumference - (safePerc / 100) * circumference;
  return (
    <div className="donut-wrap">
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e0f7f5" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      {label && (
        <div className="donut-label" style={{ transform: "none" }}>{label}</div>
      )}
    </div>
  );
};

const GaugeCircle = ({ value, max = 100, label, unit = "", color = "#00c9b7" }) => {
  const size = 88;
  const sw = 8;
  const radius = (size - sw) / 2;
  const circumference = 2 * Math.PI * radius;
  const safeVal = value == null ? 0 : Math.max(0, Math.min(max, value));
  const perc = max > 0 ? safeVal / max : 0;
  const offset = circumference - perc * circumference;
  return (
    <div className="gauge-circle-wrap">
      <div className="gauge-circle-svg-wrap">
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e0f7f5" strokeWidth={sw} />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeWidth={sw}
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }}
          />
        </svg>
        <div className="gauge-circle-value">
          {value == null ? "—" : Math.round(value)}
          {unit && <span className="gauge-unit">{unit}</span>}
        </div>
      </div>
      <div className="gauge-label">{label}</div>
    </div>
  );
};

const MachineReport = () => {
  const { getToken } = useAuth();
  const location = useLocation();
  const filters = useReportFilters();
  const navFiltersAppliedRef = useRef(false);
  const [committed, setCommitted] = useState(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    machineIds: [...filters.machineIds],
    shiftId: filters.shiftId || "",
  }));
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [breaksModalOpen, setBreaksModalOpen] = useState(false);

  const fetchMachineList = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_URL}/api/machine-live/list-machines`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setMachines(res.data.machines || []);
        setShifts(res.data.shifts || []);
      }
    } catch (e) {
      console.error("Failed to fetch machine list", e);
    }
  }, [getToken]);

  const fetchReportStats = useCallback(
    async (machineId, shiftId, date) => {
      if (!machineId) return;
      setLoading(true);
      try {
        const token = getToken();
        const params = { date };
        if (shiftId) params.shiftId = shiftId;
        const res = await axios.get(
          `${API_URL}/api/machine-live/${machineId}/report-stats`,
          { headers: { Authorization: `Bearer ${token}` }, params },
        );
        if (res.data.success) {
          setStats(res.data.data);
        }
      } catch (e) {
        console.error("Failed to fetch report stats", e);
      } finally {
        setLoading(false);
      }
    },
    [getToken],
  );

  const applyFilters = useCallback(() => {
    setCommitted({
      startDate: filters.startDate,
      endDate: filters.endDate,
      machineIds: [...filters.machineIds],
      shiftId: filters.shiftId || "",
    });
  }, [
    filters.startDate,
    filters.endDate,
    filters.machineIds,
    filters.shiftId,
  ]);

  useEffect(() => {
    fetchMachineList();
  }, [fetchMachineList]);

  useEffect(() => {
    if (navFiltersAppliedRef.current) return;
    const nav = location.state?.reportFilters;
    if (!nav?.machineId) return;

    navFiltersAppliedRef.current = true;
    const startDate = nav.startDate || todayStr();
    const endDate = nav.endDate || startDate;
    const machineId = String(nav.machineId);
    const shiftId = nav.shiftId ? String(nav.shiftId) : "";

    filters.setStartDate(startDate);
    filters.setEndDate(endDate);
    filters.setMachineIds([machineId]);
    filters.setShiftId(shiftId);
    setCommitted({
      startDate,
      endDate,
      machineIds: [machineId],
      shiftId,
    });
  }, [location.state, filters]);

  useEffect(() => {
    const machineId =
      committed.machineIds[0] || machines[0]?._id || "";
    if (machineId && committed.shiftId) {
      fetchReportStats(machineId, committed.shiftId, committed.startDate);
    } else {
      setStats(null);
    }
  }, [committed, machines, fetchReportStats]);

  const machine = stats?.machine;
  const shiftInfo = stats?.selectedShift;
  const production = stats?.production;
  const topStops = stats?.topStops || [];
  const speed = stats?.speed;
  const time = stats?.time || {};
  const oee = stats?.oee || {};

  const elapsedMs = time.elapsedMs || 0;
  const stopMs = time.stopMs || 0;
  const workMs = time.workMs || 0;
  const donutPercent = elapsedMs > 0 ? (workMs / elapsedMs) * 100 : 100;

  const liveOEE = oee.oee ?? 0;
  const liveAvailability = oee.availability ?? 100;
  const livePerformance = oee.performance ?? null;
  const liveQuality = oee.quality ?? 100;

  const machineProductLabel = (m) => {
    if (m?.productName) return m.productName;
    if (m?.productTypeCode) return m.productTypeCode;
    if (m?.productTypeName) return m.productTypeName;
    if (m?.productTypesDetail?.length) {
      return m.productTypesDetail.map((d) => d.code || d.name).join(" / ");
    }
    return "—";
  };

  return (
    <div className="machine-live-monitor">
      {/* Header */}
      <div className="mlm-header card card-body p-2">
        <div className="mlm-header-left">
          <h2 className="mlm-title">Makine Raporu</h2>
          <div className="mlm-ws-status offline" style={{ background: "#f0f9ff", color: "#0369a1", borderColor: "#bae6fd" }}>
            <FiCalendar size={14} />
            <span>Geçmiş Rapor</span>
          </div>
        </div>
        <div className="mlm-header-right">
          <ReportViewOptionsPanel
            filters={filters}
            machines={machines}
            shifts={shifts}
            onApply={applyFilters}
            showTransFilters={false}
            showModel={false}
            showMachines={machines.length > 0}
          />
        </div>
      </div>

      {/* Grid */}
      {!committed.shiftId ? (
        <div className="mlm-card text-center p-5" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', margin: '20px 0' }}>
          <FiClock size={48} style={{ color: '#94a3b8', marginBottom: '16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Vardiya Seçilmedi</h3>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Rapor verilerini görüntülemek için lütfen filtre panelinden bir vardiya seçin.</p>
        </div>
      ) : (
        <div className="mlm-grid">
        {/* Row 1 */}
        <div className="mlm-card mlm-machine-info">
          <div className="mlm-card-title">Makine</div>
          <div className="mlm-info-row">
            <span className="mlm-info-label">Adı</span>
            <span className="mlm-info-value">{machine?.name || "—"}</span>
          </div>
          <div className="mlm-info-row">
            <span className="mlm-info-label">Parça Adı</span>
            <span className="mlm-info-value mlm-info-value--part">
              <span className="mlm-part-name">
                {machineProductLabel(machine)}
              </span>
            </span>
          </div>
          <div className="mlm-info-row">
            <span className="mlm-info-label">Konum</span>
            <span className="mlm-info-value">{machine?.location || "—"}</span>
          </div>
          <div className="mlm-info-row">
            <span className="mlm-info-label">Rapor Tarihi</span>
            <span className="mlm-info-value">
              {committed.startDate || "—"}
            </span>
          </div>
        </div>

        <div className="mlm-card mlm-shift-info">
          <div className="mlm-card-title">Vardiya</div>
          {shiftInfo ? (
            <>
              <div className="mlm-shift-name">{shiftInfo.name}</div>
              <div className="mlm-shift-time-row">
                <div>
                  <div className="mlm-shift-time-label">Başlangıç - Bitiş Saati</div>
                  <div className="mlm-shift-time">
                    {shiftInfo.scheduleStartTime != null && shiftInfo.scheduleEndTime != null
                      ? `${shiftInfo.scheduleStartTime} – ${shiftInfo.scheduleEndTime}`
                      : `${formatTimeTrInAppTz(shiftInfo.startTime)} – ${formatTimeTrInAppTz(shiftInfo.endTime)}`}
                  </div>
                </div>
                <button
                  type="button"
                  className="mlm-breaks-btn"
                  title="Molalar"
                  aria-label="Molalar"
                  onClick={() => setBreaksModalOpen(true)}
                >
                  <FiClock size={12} />
                  {(shiftInfo.breaks || []).length > 0 && (
                    <span className="mlm-breaks-count">{shiftInfo.breaks.length}</span>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="mlm-no-shift">Vardiya seçilmedi</div>
          )}
        </div>

        <div className="mlm-card mlm-production-count">
          <div className="mlm-card-title">Vardiya Üretim Miktarı</div>
          <div className="mlm-big-number">
            {production?.totalShift ?? production?.total ?? 0}
          </div>
          <div className="mlm-big-number-label">Vardiyada Üretilen (Adet)</div>
          <div className="mlm-divider" />
          <div className="mlm-big-number secondary">
            {production?.targetNow == null ? "—" : production.targetNow}
          </div>
          <div className="mlm-big-number-label">Anlık Hedef (Adet)</div>
        </div>

        <div className="mlm-card mlm-last-hour">
          <div className="mlm-card-title">Son Saat Üretilen (Vardiya)</div>
          <div className="mlm-big-number">
            {production?.lastHourShift ?? production?.lastHour ?? "—"}
          </div>
          <div className="mlm-big-number-label">Adet</div>
        </div>

        {/* Row 2 */}
        <div className="mlm-card mlm-machine-status">
          <div className="mlm-card-title">Makine Durumu</div>
          <div className="mlm-donut-container">
            <DonutChart
              percentage={donutPercent}
              size={160}
              color="#00c9b7"
              label={formatDuration(workMs)}
            />
          </div>
          <div className="mlm-donut-legend">
            <span className="mlm-legend-dot" style={{ background: "#00c9b7" }} />
            <span>Vardiya Çalışma Süresi</span>
          </div>
        </div>

        <div className="mlm-card mlm-time-stats">
          <div className="mlm-card-title">Duruş / Çalışma / Toplam Süre</div>
          <div className="mlm-time-row">
            <div className="mlm-time-value">{formatDuration(stopMs)}</div>
            <div className="mlm-time-label">Vardiyadaki Toplam Duruş Süresi</div>
          </div>
          <div className="mlm-time-row">
            <div className="mlm-time-value">{formatDuration(workMs)}</div>
            <div className="mlm-time-label">Vardiyadaki Toplam Çalışma Süresi</div>
          </div>
          <div className="mlm-time-row">
            <div className="mlm-time-value">{formatDuration(elapsedMs)}</div>
            <div className="mlm-time-label">Vardiya Toplam Süre</div>
          </div>
        </div>

        <div className="mlm-card mlm-top-stops">
          <div className="mlm-card-title">Vardiyadaki En Büyük 5 Duruş</div>
          {topStops.length === 0 ? (
            <div className="mlm-empty-stops">Bu vardiyadaki duruş kaydı yok</div>
          ) : (
            <div className="mlm-stops-table-wrap">
              <table className="mlm-stops-table">
                <thead>
                  <tr>
                    <th>Duruş Adı</th>
                    <th>Başlangıç Tarihi</th>
                    <th>Bitiş Tarihi</th>
                    <th>Duruş Süresi</th>
                    <th>Dakika</th>
                  </tr>
                </thead>
                <tbody>
                  {topStops.map((stop, idx) => (
                    <tr key={stop._id || idx}>
                      <td>{stop.stopReason?.name || "Belirsiz Duruş"}</td>
                      <td>{formatDateTimeTrInAppTz(stop.startTime)}</td>
                      <td>
                        {stop.endTime ? (
                          formatDateTimeTrInAppTz(stop.endTime)
                        ) : (
                          <span className="mlm-ongoing">Devam ediyor</span>
                        )}
                      </td>
                      <td>{formatDuration(stop.durationSeconds * 1000)}</td>
                      <td>{stop.durationMinutes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Row 3 */}
        <div className="mlm-card mlm-oee">
          <div className="mlm-card-title">OEE</div>
          <div className="mlm-gauges-row">
            <GaugeCircle value={liveOEE} max={100} label="OEE" color="#00c9b7" />
            <GaugeCircle value={liveAvailability} max={100} label="Kullanılabilirlik" color="#00c9b7" />
            <GaugeCircle
              value={livePerformance}
              max={100}
              label="Performans"
              color={livePerformance != null ? "#00c9b7" : "#94a3b8"}
            />
            <GaugeCircle value={liveQuality} max={100} label="Kalite" color="#00c9b7" />
          </div>
        </div>

        <div className="mlm-card mlm-speed-nostop">
          <div className="mlm-card-title">
            Vardiya Hız (Duruşsuz) <small>DAKİKA/ADET</small>
          </div>
          <div className="mlm-big-number">
            {speed?.noStopSpeed != null ? speed.noStopSpeed : "—"}
          </div>
          <div className="mlm-big-number-label">Duruşsuz Hız</div>
        </div>

        <div className="mlm-card mlm-speed-total">
          <div className="mlm-card-title">
            Vardiya Hız (Toplam) <small>DAKİKA/ADET</small>
          </div>
          <div className="mlm-big-number">
            {speed?.totalSpeed != null ? speed.totalSpeed : "—"}
          </div>
          <div className="mlm-big-number-label">Toplam Hız</div>
        </div>
      </div>
      )}

      <Modal
        isOpen={breaksModalOpen}
        onClose={() => setBreaksModalOpen(false)}
        width="small"
      >
        <ModalHeader>
          {shiftInfo?.name ? `${shiftInfo.name} — Molalar` : "Molalar"}
        </ModalHeader>
        <ModalBody className="mlm-breaks-modal-body" showCloseButton={false}>
          {(shiftInfo?.breaks || []).length === 0 ? (
            <div className="mlm-breaks-empty">Bu vardiya için mola tanımlı değil</div>
          ) : (
            <div className="mlm-breaks-list">
              {(shiftInfo?.breaks || []).map((br, idx) => (
                <div key={br._id || idx} className="mlm-break-row">
                  <span className="mlm-break-name">{br.name}</span>
                  <span className="mlm-break-time">{br.startTime} – {br.endTime}</span>
                </div>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button appearance="primary-subtle" onClick={() => setBreaksModalOpen(false)}>
            Kapat
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default MachineReport;
